import Core            from "@/core/Core";
import PartnersDAO     from "@/domains/Partners/PartnersDAO";
import Partner         from "@/domains/Partners/Partner";

import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort       from "@/core/services/Rpc/RpcDataSort";
import type { RequestOptions } from "@/core/services/Rpc/RpcService";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";

class PartnersService {

  static isOperationsAllowedList = [
    { id: true,  name: 'Полный'   },
    { id: false, name: 'Тестовый' },
  ];

  static partnerTypeList = [
    { id: 'seller',    name: 'Поставщик'             },
    { id: 'buyer',     name: 'Покупатель'            },
    { id: 'deliverer', name: 'Транспортная компания' },
  ];

  static getEditSectionTabs(isBuyer: boolean) {
    if (isBuyer) {
      return [
        { id: 'persons',           name: 'Контакты'              },
        { id: 'main',              name: 'Основное'              },
        { id: 'supplyOffers',      name: 'Разрешения на поставку' },
        { id: 'files',             name: 'Файлы'                 },
        { id: 'orders',            name: 'История заказов'       },
      ];
    } else {
      return [
        { id: 'contacts', name: 'Контакты' },
        { id: 'main',     name: 'Основное' },
      ];
    }
  }

  /** ------ Чтение ------ */

  /**
   * Загрузить одного партнёра по ID
   */
  static async get(id: number): Promise<Partner | null> {
    const response = await PartnersDAO.get(id);

    return Core.utils.hydrate(response.data, Partner.fromServer);
  }

  /**
   * Пагинированный список — формат под DataGridLoader.
   *
   * options — опции запроса (abortPrevious, signal, timeout), уходят в DAO как есть.
   *
   * options сейчас лежат в params на одном уровне с фильтрами, pagination и sort. Это решение
   * не окончательное и, возможно, неверное: опции запроса — инфраструктурный уровень (транспорт,
   * отмена, таймаут), а фильтры — данные, и смешивать их в одном объекте не обязательно.
   */
  static async getListPaginated(
    params?: Parameters<typeof PartnersDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?:       RpcDataSort;
      options?:    RequestOptions;
    }
  ): Promise<{ data: Partner[]; pagination: RpcDataPagination }> {
    const { pagination, sort, options, ...filters } = params ?? {};
    const response = await PartnersDAO.getList(filters, pagination, sort, options);

    return {
      data:       Core.utils.hydrateArray(response.data, Partner.fromServer),
      pagination: response.meta.pagination,
    };
  }

  /**
   * Плоский список партнёров. Для не-гридовых сценариев (диалоги, автокомплиты).
   */
  static async getList(
    params?: Parameters<typeof PartnersDAO.getList>[0] & { options?: RequestOptions }
  ): Promise<Partner[]> {
    return (await this.getListPaginated(params)).data;
  }

  /**
   * Поиск продавцов по строке. Используется как searchFunction
   * в фильтрах реестров и автокомплитах.
   *
   * Новый поиск отменяет предыдущий, ещё не вернувшийся (abortPrevious): двух
   * поисков продавцов одновременно на странице не бывает. Запросы partners.getList
   * без abortPrevious (гриды, прелоады) не отменяются.
   */
  static searchSellers(pattern: string): Promise<Partner[]> {
    return PartnersService.getList({ pattern, types: ['seller'], options: { abortPrevious: true } });
  }

  /**
   * Поиск покупателей по строке.
   */
  static searchBuyers(pattern: string): Promise<Partner[]> {
    return PartnersService.getList({ pattern, types: ['buyer'] });
  }

  /** ------ CRUD ------ */

  static async add(partner: Partner): Promise<boolean> {
    const response = await PartnersDAO.add(partner);

    if (response.ok) {
      partner.id = response.data.id;
      Core.showNotification.success('Партнёр успешно добавлен');
    }

    return response.ok;
  }

  static async update(partner: Partner): Promise<boolean> {
    const response = await PartnersDAO.update(partner);

    if (response.ok) {
      Core.showNotification.success('Партнёр успешно обновлён');
    }

    return response.ok;
  }

  static async delete(id: number): Promise<boolean> {
    const response = await PartnersDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Партнёр успешно удалён');
    }

    return response.ok;
  }

  static async deleteWithConfirm(partner: Partner): Promise<boolean> {
    const confirmed = await Core.confirm('Подтверждаете удаление партнёра?');

    if (confirmed) {
      return await this.delete(partner.id);
    } else {
      return false;
    }
  }

  /**
   * Показывает карточку партнёра. Создание и редактирование — один вход:
   * partnerOrId — существующий партнёр/id, null — создание нового.
   * Промис резолвится по закрытию карточки — вызывающий сам решает, рефрешить ли
   * список.
   */
  static showPartnerCard(partnerOrId: Partner | number | null): Promise<boolean> {
    return Core.dialogs.PartnerCard.showAsPromise({ partnerOrId });
  }

  /**
   * Открывает карточку партнёра в режиме создания.
   * Возвращает созданного партнёра либо null, если оператор закрыл карточку.
   *
   * Карточка резолвится булевым и созданную сущность через промис не отдаёт,
   * поэтому забираем её событием partner:created — промис собирается здесь.
   */
  static createPartner(): Promise<Partner | null> {
    return new Promise((resolve) => {
      Core.dialogs.PartnerCard.show(
        { partnerOrId: null },
        {
          'partner:created': (partner: Partner) => { resolve(partner); },
          close:             () => { resolve(null); },
        },
      );
    });
  }

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (partner: Partner) => { return this.showPartnerCard(partner); },
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (partner: Partner) => { return this.deleteWithConfirm(partner); },
        (partner: Partner) => partner.getReasonCannotDelete(),
      ),
    ];
  }
}

export default PartnersService;
